/** Shared Design tab + website editor font catalogs. */

export const FINDER_FONT_FAMILY =
  '"Finder", system-ui, -apple-system, sans-serif';

/** Website Default pairing — Futura with Century Gothic / Jost fallbacks. */
export const FUTURA_FONT_FAMILY =
  "Futura, 'Century Gothic', 'Jost', sans-serif";

export type DesignFontOption = {
  name: string;
  value: string;
  /** When true, eligible as a body / secondary font. */
  body: boolean;
};

export const FONT_OPTIONS: DesignFontOption[] = [
  { name: "Inter", value: "Inter, sans-serif", body: true },
  { name: "Poppins", value: "Poppins, sans-serif", body: true },
  { name: "Nunito Sans", value: "'Nunito Sans', sans-serif", body: true },
  { name: "Space Grotesk", value: "'Space Grotesk', sans-serif", body: true },
  { name: "Lora", value: "Lora, serif", body: true },
  { name: "Space Mono", value: "'Space Mono', monospace", body: true },
  { name: "Hagrid", value: "'Hagrid', sans-serif", body: false },
  {
    name: "Bricolage Grotesque",
    value: "Bricolage Grotesque, sans-serif",
    body: false,
  },
  { name: "Fraunces", value: "Fraunces, serif", body: false },
  { name: "Marcellus", value: "Marcellus, serif", body: false },
  { name: "Oswald", value: "Oswald, sans-serif", body: false },
  { name: "Playfair Display", value: "Playfair Display, serif", body: false },
  { name: "Gloock", value: "Gloock, serif", body: false },
  { name: "DM Serif Display", value: "'DM Serif Display', serif", body: false },
  { name: "Syne", value: "Syne, sans-serif", body: false },
  {
    name: "Instrument Serif",
    value: "'Instrument Serif', Georgia, serif",
    body: false,
  },
  { name: "Work Sans", value: "'Work Sans', sans-serif", body: true },
  {
    name: "Cormorant Garamond",
    value: "'Cormorant Garamond', Georgia, serif",
    body: false,
  },
  { name: "Jost", value: "Jost, sans-serif", body: true },
  { name: "DM Sans", value: "'DM Sans', sans-serif", body: true },
  {
    name: "Futura",
    value: FUTURA_FONT_FAMILY,
    body: true,
  },
  {
    name: "Finder",
    value: FINDER_FONT_FAMILY,
    body: true,
  },
];

/** Link-in-bio Design tab still defaults to Finder (Browsable brand face). */
export const DESIGN_DEFAULT_FONT_INDEX = FONT_OPTIONS.findIndex(
  (f) => f.name === "Finder",
);

export type DesignFontPairOption = {
  label: string;
  primary: string;
  secondary: string;
  /** Shown under the pairing name in the design drawer. */
  sub?: string;
  /** TITLE — site name / headings. */
  displayWeight?: number;
  displayTracking?: string;
  /**
   * TITLE size vs the 27px pairing specimen.
   * Instrument Serif 31px ≈ 1.15; Cormorant 34px ≈ 1.26.
   */
  displaySizeScale?: number;
  /** NAV — Home / Products / Content / Search. */
  navWeight?: number;
  /** CATEGORIES — Women's / Men's / Beauty. */
  categoryWeight?: number;
  /** CAPTION — product names ("Real Soft Wrap Robe"). */
  captionWeight?: number;
  /** TYPE — brand / type labels ("Aerie"). */
  typeWeight?: number;
};

/** Fallback roles when a pairing (or Custom) omits a weight. */
export const DEFAULT_FONT_PAIR_ROLES = {
  displayWeight: 400,
  displayTracking: "0",
  displaySizeScale: 1,
  navWeight: 500,
  categoryWeight: 300,
  captionWeight: 400,
  typeWeight: 400,
} as const;

export type FontPairRoles = {
  displayWeight: number;
  displayTracking: string;
  displaySizeScale: number;
  navWeight: number;
  categoryWeight: number;
  captionWeight: number;
  typeWeight: number;
};

export function resolveFontPairRoles(
  pair?: Pick<
    DesignFontPairOption,
    | "displayWeight"
    | "displayTracking"
    | "displaySizeScale"
    | "navWeight"
    | "categoryWeight"
    | "captionWeight"
    | "typeWeight"
  > | null,
): FontPairRoles {
  return {
    displayWeight: pair?.displayWeight ?? DEFAULT_FONT_PAIR_ROLES.displayWeight,
    displayTracking:
      pair?.displayTracking ?? DEFAULT_FONT_PAIR_ROLES.displayTracking,
    displaySizeScale:
      pair?.displaySizeScale ?? DEFAULT_FONT_PAIR_ROLES.displaySizeScale,
    navWeight: pair?.navWeight ?? DEFAULT_FONT_PAIR_ROLES.navWeight,
    categoryWeight:
      pair?.categoryWeight ?? DEFAULT_FONT_PAIR_ROLES.categoryWeight,
    captionWeight: pair?.captionWeight ?? DEFAULT_FONT_PAIR_ROLES.captionWeight,
    typeWeight: pair?.typeWeight ?? DEFAULT_FONT_PAIR_ROLES.typeWeight,
  };
}

/**
 * Chandler's six pairings from font-pairings.html.
 * Five roles per pairing: title, nav, categories, caption, type.
 * Futura (current production face) sits first as Default; the rest load from Google Fonts.
 */
export const FONT_PAIR_OPTIONS: DesignFontPairOption[] = [
  {
    label: "Default",
    sub: "Futura throughout",
    primary: FUTURA_FONT_FAMILY,
    secondary: FUTURA_FONT_FAMILY,
    displayWeight: 400,
    displayTracking: "0.02em",
    displaySizeScale: 1,
    navWeight: 400,
    categoryWeight: 400,
    captionWeight: 400,
    typeWeight: 400,
  },
  {
    label: "Instrument Serif + Inter",
    sub: "Instrument Serif · Inter",
    primary: "'Instrument Serif', Georgia, serif",
    secondary: "Inter, sans-serif",
    displayWeight: 400,
    displayTracking: "0",
    displaySizeScale: 1.15,
    navWeight: 500,
    categoryWeight: 300,
    captionWeight: 400,
    typeWeight: 400,
  },
  {
    label: "Fraunces + Work Sans",
    sub: "Fraunces · Work Sans",
    primary: "Fraunces, serif",
    secondary: "'Work Sans', sans-serif",
    displayWeight: 600,
    displayTracking: "0",
    displaySizeScale: 1,
    navWeight: 500,
    categoryWeight: 300,
    captionWeight: 400,
    typeWeight: 400,
  },
  {
    label: "Cormorant Garamond + Jost",
    sub: "Cormorant Garamond · Jost",
    primary: "'Cormorant Garamond', Georgia, serif",
    secondary: "Jost, sans-serif",
    displayWeight: 300,
    displayTracking: "0.01em",
    displaySizeScale: 1.26,
    navWeight: 500,
    categoryWeight: 300,
    captionWeight: 400,
    typeWeight: 400,
  },
  {
    label: "Poppins + Nunito Sans",
    sub: "Poppins · Nunito Sans",
    primary: "Poppins, sans-serif",
    secondary: "'Nunito Sans', sans-serif",
    displayWeight: 500,
    displayTracking: "0",
    displaySizeScale: 1,
    navWeight: 600,
    categoryWeight: 300,
    captionWeight: 400,
    typeWeight: 400,
  },
  {
    label: "Bricolage Grotesque + DM Sans",
    sub: "Bricolage Grotesque · DM Sans",
    primary: "Bricolage Grotesque, sans-serif",
    secondary: "'DM Sans', sans-serif",
    displayWeight: 600,
    displayTracking: "-0.01em",
    displaySizeScale: 1,
    navWeight: 500,
    categoryWeight: 300,
    captionWeight: 400,
    typeWeight: 400,
  },
];

/** Saved pairing labels from earlier catalogs → current FONT_PAIR_OPTIONS labels. */
export const FONT_PAIR_ALIASES: Record<string, string> = {
  "Bricolage Grotesque + Inter": "Bricolage Grotesque + DM Sans",
  "Poppins + Poppins": "Poppins + Nunito Sans",
  "Fraunces + Inter": "Fraunces + Work Sans",
  "Playfair Display + Inter": "Cormorant Garamond + Jost",
  "Gloock + Inter": "Instrument Serif + Inter",
  "Inter + Inter": "Instrument Serif + Inter",
  "Hagrid + Inter": "Default",
};

/**
 * Link-in-bio Design tab pairings. Same list as the website catalog, but
 * Default stays Finder (Browsable brand face) instead of Futura.
 */
export const DESIGN_FONT_PAIR_OPTIONS: DesignFontPairOption[] = [
  {
    label: "Default",
    sub: "Finder throughout",
    primary: FINDER_FONT_FAMILY,
    secondary: FINDER_FONT_FAMILY,
    displayWeight: 400,
    displayTracking: "0.02em",
    displaySizeScale: 1,
    navWeight: 400,
    categoryWeight: 400,
    captionWeight: 400,
    typeWeight: 400,
  },
  ...FONT_PAIR_OPTIONS.filter((p) => p.label !== "Default"),
];

export function resolveFontPairLabel(label: string | null | undefined): string {
  const raw = (label || "Default").trim() || "Default";
  return FONT_PAIR_ALIASES[raw] || raw;
}

export const DESIGN_CUSTOM_FONT_PAIR_KEY = "Custom";
